import type { Comment, CommentListOrder } from '@/@types/comment'
import type { Post } from '@/@types/post'
import type { CommentData } from '@/components/Comment/Comment'
import { CommentActionsProvider } from '@/components/Comment/CommentActionsContext'
import CommentForm from '@/components/Comment/CommentForm'
import {
	DELETE_COMMENT_MUTATION_OPTIONS,
	LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS,
	PIN_COMMENT_TOGGLE_MUTATION_OPTIONS,
} from '@/components/Comment/hooks'
import { ReplyToComment } from '@/components/Comment/types'
import { showCommentFormModal } from '@/components/Comment/utils'
import NotFoundPage from '@/components/NotFoundPage'
import Page from '@/components/Page'
import CommentListSection from '@/components/Post/CommentListSection'
import { PostActionsProvider } from '@/components/Post/PostActionsContext'
import PostCard from '@/components/Post/PostCard'
import { checkIsInJikeApp } from '@/utils/env'
import {
	generateCommentQueryKey,
	generatePrimaryCommentsQueryKey,
	getPrimaryCommentsQueryOptions,
} from '@/utils/fetchers/comment'
import { fetchPost } from '@/utils/fetchers/post'
import usePost from '@/utils/hooks/usePost'
import { prepareToRefetchListQuery } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import { ROUTE_TO_POST_TYPE_MAPPING } from '@/utils/route'
import { css } from '@emotion/react'
import { modals } from '@mantine/modals'
import { Loading } from '@ruguoapp/jike-fe-components'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { type HistoryState, createFileRoute, notFound, useLocation } from '@tanstack/react-router'
import { flatMap, isNil, map, pick } from 'lodash-es'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTitle } from 'react-use'

export interface PostPageHistoryState extends HistoryState {
	isLocateToComments: boolean
}

export const Route = createFileRoute('/u/$username_/$type/$id/')({
	component: PostPage,
	beforeLoad: async ({ matches }) => {
		checkIsInJikeApp(matches)
	},
	loader: async ({ params: { id, type } }) => ({
		post: await fetchPost(
			id,
			ROUTE_TO_POST_TYPE_MAPPING[type as keyof typeof ROUTE_TO_POST_TYPE_MAPPING]
		)
			.then((data: Post | undefined) => {
				if (isNil(data)) {
					throw notFound()
				}
				return data
			})
			.catch(() => {
				throw notFound()
			}),
	}),
})

// Post Page

function PostPage() {
	const { id } = Route.useParams()
	const hash = useLocation({
		select: (location) => {
			return location.hash
		},
	})
	const alignCommentListSectionAnchorRef = useRef<HTMLDivElement>(null)

	const post = usePost(id)!
	const [order, setOrder] = useState<CommentListOrder>('LIKES')
	const [dummyComments, setDummyComments] = useState<Comment[]>([])

	const { data, fetchNextPage, refetch } = useInfiniteQuery(
		getPrimaryCommentsQueryOptions({
			targetId: post.id,
			targetType: post.type,
			order,
		})
	)
	const refresh = useCallback(() => {
		prepareToRefetchListQuery(
			generatePrimaryCommentsQueryKey({
				targetId: post.id,
				targetType: post.type,
				order,
			})
		)
		return refetch()
	}, [refetch, post, order])

	const comments = useMemo(() => {
		return map(
			[...dummyComments, ...flatMap(data?.pages, (page) => page.data)],
			(item) => pick(item, ['id', 'targetType']) as CommentData
		)
	}, [dummyComments, data])

	const isLocateToComments = hash === 'comments' && comments.length > 0

	const { mutateAsync: toggleLike } = useMutation(LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS)
	const { mutateAsync: togglePin } = useMutation(PIN_COMMENT_TOGGLE_MUTATION_OPTIONS)
	const { mutateAsync: deleteComment } = useMutation(DELETE_COMMENT_MUTATION_OPTIONS)
	const handleCommentReply = (toComment: ReplyToComment) => {
		const modalId = showCommentFormModal({
			replyToComment: toComment,
			onClose: () => {
				modals.close(modalId)
			},
			onSubmit: refresh,
		})
	}

	useTitle(`${post.user?.screenName}：${post.content} - 即刻`)

	useEffect(() => {
		if (isLocateToComments) {
			setTimeout(() => {
				alignCommentListSectionAnchorRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			}, 500)
		}
	}, [isLocateToComments])

	return (
		<Page title="动态详情" onReachPageBottom={fetchNextPage} css={css``}>
			{post.deleted ? (
				<NotFoundPage />
			) : (
				<div
					css={css`
						display: grid;
						grid-template-rows: auto auto 1fr;
						gap: 20px;
						overflow: hidden;
						width: 100%;
					`}
				>
					<PostActionsProvider
						value={{
							onComment: (post) => {
								const modalId = showCommentFormModal({
									replyToPost: post,
									onClose: () => {
										modals.close(modalId)
									},
									onSubmit: refresh,
								})
							},
						}}
					>
						<PostCard
							mode="full"
							data={post}
							css={css`
								padding: 10px 15px;
							`}
						/>
					</PostActionsProvider>

					<Suspense fallback={<Loading />}>
						<CommentForm
							targetType={post.type}
							targetId={post.id}
							onSubmit={(newComment) => {
								queryClient.setQueryData(
									generateCommentQueryKey(pick(newComment, ['id', 'targetType'])),
									newComment
								)
								setDummyComments((prev) => [newComment, ...(prev ?? [])])
							}}
							css={css`
								padding: 0 15px;
							`}
						/>
					</Suspense>

					<section
						css={css`
							position: relative;
						`}
					>
						<div
							ref={alignCommentListSectionAnchorRef}
							css={css`
								position: absolute;
								top: -22px;
								left: 0;
								height: 0;
								pointer-events: none;
							`}
						/>

						<Suspense fallback={<Loading />}>
							<CommentActionsProvider
								value={{
									refresh,
									onDelete: deleteComment,
									onToggleLike: toggleLike,
									onTogglePin: togglePin,
									onComment: handleCommentReply,
								}}
							>
								<CommentListSection data={comments} order={order} onOrderChange={setOrder} />
							</CommentActionsProvider>
						</Suspense>
					</section>
				</div>
			)}
		</Page>
	)
}
